/**
 * @fileoverview ModuleHeader — the Workshop module's one optional persistent
 * top toolbar (docs: Core concepts → Layouts → Header). A module LAYER, sibling
 * of the Page — owned and rendered by the module, NOT the shell.
 *
 * Composes the existing `AppHeader` zone layout so the chrome is identical to
 * the (transitional) shell header, but the title + actions come from the module:
 *   start  → app identity (IronCase mark, → home) + ModuleTitle (the title)
 *   middle → global ⌘J search
 *   end    → module actions (a Button Group). Theme toggle lives in the account
 *            menu (rail), not the header.
 *
 * Per the official Header config surface, `title` is the module's name (it also
 * stands in for the browser/Carbon tab name elsewhere).
 */

import type { ReactElement, ReactNode } from "react";
import { useNavigate } from "react-router";

import { AppHeader } from "../app-header";
import { IronCaseBrandIcon, SearchTrigger } from "../header-chrome";

interface ModuleHeaderProps {
  /** The module name — the largest heading; also the tab name. */
  title: string;
  /** Optional breadcrumb trail (depth within the module). */
  breadcrumbs?: ReactNode;
  /** Module-level actions (a Button Group) routed into the end zone, before the theme toggle. */
  actions?: ReactNode;
}

export function ModuleHeader({ title, breadcrumbs, actions }: ModuleHeaderProps): ReactElement {
  const navigate = useNavigate();
  return (
    <AppHeader
      appIcon={<IronCaseBrandIcon />}
      appIconAriaLabel="IronCase home"
      onAppHomeClick={() => navigate("/")}
      moduleName={title}
      breadcrumbs={breadcrumbs}
      center={<SearchTrigger />}
      actions={actions}
    />
  );
}
